<template>
  <div>
    <selected-award-amount-icon
      ref="cartInfo"
      :cartUrl="awardCartFormUrl"
    ></selected-award-amount-icon>
    <div
        v-if="award && hasInsufficientPoints"
        class="col-12 error-box mb-3"
        v-text="translate('lblInsufficientPoints')"
      ></div>
    <div v-if="award" class="award-detail">
      <div class="details">
        <image-zoom :preview="award.teaser_image" :zoomed="award.image" :alt="translate('lblProductImageAlt')"></image-zoom>
        <div class="text">
          <h1 class="h2" v-html="award.title"></h1>
          <p v-if="award.teaser_text" v-html="award.teaser_text"></p>
        </div>
      </div>
      <div class="award-detail--checkout">
        <form @submit.prevent="checkOut" ref="form" class="no-validate">
          <div class="award-detail--checkout--variety" v-if="award.variants && award.variants.length > 0">
            <label :for="`${_uid}-variety`" v-text="translate('lblVariety')"></label>
            <select :id="`${_uid}-variety`" name="variety"
                    v-model="currentVariant" required="required"
                    :class="{ 'user-error': showErrors && errors.variety && !hasInsufficientPoints }"
                    @change="checkValidityState"
                    :disabled="hasInsufficientPoints"
            >
              <option value="" selected="selected" v-text="translate('lblVarietyDropdown')"></option>
              <option
                v-for="variant in award.variants" :key="variant.articlenumber"
                :value="variant.articlenumber"
                v-text="variant.title"
              ></option>
            </select>
          </div>
          <div class="award-detail--checkout--quantity">
            <label :for="`${_uid}-quantity`" v-text="translate('lblQuantity')"></label>
            <input
              :id="`${_uid}-quantity`" type="number" v-model="quantity" name="quantity"
              inputMode="numeric" pattern="[0-9]*" min="1" max="999"
              :class="{ 'user-error': showErrors && errors.quantity }"
              @change="checkValidityState"
            />
          </div>
          <div class="award-detail--checkout--price">
            <span v-text="translate('lblPrice')"></span>
            <span v-text="award.points"></span>
          </div>
          <button
            type="submit"
            :class="['button button--medium button--primary', (showErrors && hasErrors) || hasInsufficientPoints ? 'disabled grey' : null]"
            @click="checkValidityState(true)"
            v-text="translate('btnAddToAwardCheckoutCart')"
            :disabled="hasInsufficientPoints"
          ></button>
        </form>
      </div>
    </div>
    <div v-if="apiError" class="col-12 error-box mb-3" v-text="translate('lblApiError')"></div>
  </div>
</template>

<script>
import LoyaltyApiConnectorMixin from './mixins/LoyaltyApiConnectorMixin';
import ImageZoom from './components/ImageZoom.vue';
import SelectedAwardAmountIcon from './components/SelectedAwardAmountIcon.vue';

export default {
  name: 'AwardDetail',
  data: () => ({
    award: null,
    quantity: 1,
    apiError: false,
    currentVariant: '',
    showErrors: false,
    errors: {},
    points: 0,
    hasInsufficientPoints: true,
  }),
  props: {
    translations: Object,
    awardCartFormUrl: String,
  },
  computed: {
    hasErrors() {
      return Object.values(this.errors).some((value) => value);
    },
  },
  methods: {
    checkValidityState(showErrors) {
      if (showErrors) {
        this.showErrors = true;
      }
      this.errors = {};
      [...this.$refs.form.querySelectorAll('select, input')].forEach((field) => {
        this.errors[field.name] = !field.validity.valid;
      });
      return !this.hasErrors;
    },
    addAwardItemToCard() {
      const storedCartItems = JSON.parse(sessionStorage.getItem('selectedAwardItems')) || [];
      const found = storedCartItems.find((item) => item.selectedVariety === this.currentVariant
        && this.award.entity_id === item.product.entity_id);
      if (found) {
        found.amount = parseInt(found.amount, 10) + parseInt(this.quantity, 10);
      } else {
        storedCartItems.push({
          product: this.award,
          amount: parseInt(this.quantity, 10),
          selectedVariety: this.currentVariant,
        });
      }
      window.sessionStorage.setItem('selectedAwardItems', JSON.stringify(storedCartItems));
      this.$refs.cartInfo.updateNumberOfCartItems();
    },
    checkOut() {
      if (!this.checkValidityState()) {
        return;
      }
      this.addAwardItemToCard();
    },
    checkPoints() {
      this.hasInsufficientPoints = this.award?.points * this.quantity > this.points;
    },
  },
  async created() {
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('award')) {
      const awards = await this.getAwardsApiResult();

      if (awards) {
        const found = awards.filter(
          (award) => `${award.entity_id}` === urlParams.get('award'),
        );
        if (found && found.length) {
          // console.log(found);
          [this.award] = found;
        }
        this.award.href = `${this.detailView}?award=${this.award.entity_id}`;
        // START TEST (remove before git, for testing purposes only)
        // this.award.teaser_image = "https://placeholder.workbox.de/1280x720";
        // this.award.image = "https://placeholder.workbox.de/1920x1080";
        // END TEST
        // console.log(this.award);
      }

      if (urlParams.has('amount')) {
        this.quantity = urlParams.get('amount');
      }

      if (urlParams.has('variant')) {
        this.currentVariant = urlParams.get('variant');
      }
    }
    const result = await this.getUserApiResult();
    if (result.point_summary) {
      this.points = result.point_summary.point_sum;
      this.checkPoints();
    }
  },
  watch: {
    quantity() {
      this.checkPoints();
    },
  },
  mixins: [LoyaltyApiConnectorMixin],
  components: {
    SelectedAwardAmountIcon,
    ImageZoom,
  },
};
</script>

<style lang="scss">
.award-detail {
  display: flex;
  align-items: flex-start;
  gap: rem(20px);

  @media (max-width: rem(767px)) {
    flex-wrap: wrap;
  }

  .details {
    flex-grow: 1;
    flex-shrink: 5;
    display: flex;
    flex-wrap: wrap;
    gap: rem(20px);
    flex-basis: rem(680px);

    .image-zoom {
      @media (max-width: rem(767px)) {
        flex-grow: 1;
      }
      @media (min-width: rem(768px)) {
        min-width: rem(160px);
        flex-basis: rem(160px);
      }

      img {
        width: 100%;
      }
    }

    @media (min-width: rem(1280px)) {
      flex-wrap: nowrap;
      align-items: flex-start;
    }

    .text {
      flex-grow: 1;

      p {
        margin-bottom: 0;
      }
    }
  }

  &--checkout {
    flex-grow: 1;
    flex-shrink: 2;
    flex-basis: rem(320px);

    div {
      background-color: $color-kws-light-gray-50;
      padding: rem(10px);

      &.award-detail--checkout--variety {
        label {
          white-space: nowrap;
          display: block;
          margin-bottom: rem(6px);
        }
      }

      &.award-detail--checkout--variety,
      &.award-detail--checkout--quantity {
        padding-bottom: 0;
      }

      &.award-detail--checkout--quantity,
      &.award-detail--checkout--price {
        display: flex;
        gap: rem(10px);
        justify-content: space-between;
        align-items: baseline;
      }
      &.award-detail--checkout--quantity {
        input {
          flex-basis: rem(100px);
          padding: 0 0.625rem;
        }
      }
      &.award-detail--checkout--price {
        font-weight: bold;

        & > span:last-child {
          font-size: 1.25rem;
          line-height: 1.2;
        }
      }
    }

    button {
      width: 100%;
      margin-top: rem(20px);
    }
  }
}
</style>
